import type { ThemeConfig } from 'antd';
import { designTokens } from './tokens';

/**
 * Ant Design Theme Configuration
 *
 * Modern SaaS design system with curvy aesthetics
 * Inspired by Linear, Stripe, and Notion
 */
export const antdTheme: ThemeConfig = {
  token: {
    // Color system
    colorPrimary: designTokens.colors.primary[600],
    colorSuccess: designTokens.colors.success.main,
    colorWarning: designTokens.colors.warning.main,
    colorError: designTokens.colors.error.main,
    colorInfo: designTokens.colors.info.main,

    // Border radius (curvy modern design)
    borderRadius: designTokens.radius.md,
    borderRadiusLG: designTokens.radius.lg,
    borderRadiusSM: designTokens.radius.sm,
    borderRadiusXS: designTokens.radius.xs,

    // Typography
    fontFamily: designTokens.typography.fontFamily.sans,
    fontSize: designTokens.typography.fontSize.sm,
    fontSizeHeading1: designTokens.typography.fontSize['4xl'],
    fontSizeHeading2: designTokens.typography.fontSize['3xl'],
    fontSizeHeading3: designTokens.typography.fontSize['2xl'],
    fontSizeHeading4: designTokens.typography.fontSize.xl,
    fontSizeHeading5: designTokens.typography.fontSize.lg,

    // Spacing
    padding: designTokens.spacing[4],
    paddingLG: designTokens.spacing[6],
    paddingSM: designTokens.spacing[3],
    paddingXS: designTokens.spacing[2],
    paddingXXS: designTokens.spacing[1],

    margin: designTokens.spacing[4],
    marginLG: designTokens.spacing[6],
    marginSM: designTokens.spacing[3],
    marginXS: designTokens.spacing[2],
    marginXXS: designTokens.spacing[1],

    // Shadows (soft elevation)
    boxShadow: designTokens.shadows.sm,
    boxShadowSecondary: designTokens.shadows.xs,

    // Line height
    lineHeight: designTokens.typography.lineHeight.normal,
    lineHeightHeading1: designTokens.typography.lineHeight.tight,
    lineHeightHeading2: designTokens.typography.lineHeight.tight,

    // Motion
    motionDurationFast: '0.15s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    motionEaseOut: 'cubic-bezier(0, 0, 0.2, 1)',

    // Control heights
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,

    // Neutral colors
    colorBgContainer: designTokens.colors.neutral[0],
    colorBgElevated: designTokens.colors.neutral[0],
    colorBgLayout: designTokens.colors.neutral[50],
    colorBorder: designTokens.colors.neutral[200],
    colorBorderSecondary: designTokens.colors.neutral[100],

    // Text colors
    colorText: designTokens.colors.neutral[900],
    colorTextSecondary: designTokens.colors.neutral[600],
    colorTextTertiary: designTokens.colors.neutral[400],
    colorTextQuaternary: designTokens.colors.neutral[300],
  },

  components: {
    // Button
    Button: {
      borderRadius: designTokens.radius.lg,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      paddingContentHorizontal: designTokens.spacing[5],
      fontWeight: designTokens.typography.fontWeight.medium,
      boxShadow: designTokens.shadows.sm,
    },

    // Input
    Input: {
      borderRadius: designTokens.radius.md,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      paddingBlock: designTokens.spacing[2],
      paddingInline: designTokens.spacing[3],
    },

    // Select
    Select: {
      borderRadius: designTokens.radius.md,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
    },

    // Card
    Card: {
      borderRadius: designTokens.radius.xl,
      boxShadow: designTokens.shadows.md,
      paddingLG: designTokens.spacing[6],
    },

    // Modal
    Modal: {
      borderRadius: designTokens.radius.xl,
      boxShadow: designTokens.shadows.xxl,
      paddingContentHorizontal: designTokens.spacing[6],
      paddingMD: designTokens.spacing[6],
    },

    // Dropdown
    Dropdown: {
      borderRadius: designTokens.radius.lg,
      boxShadow: designTokens.shadows.lg,
      paddingBlock: designTokens.spacing[2],
    },

    // Table
    Table: {
      borderRadius: designTokens.radius.lg,
      headerBg: designTokens.colors.neutral[50],
      headerBorderRadius: designTokens.radius.lg,
    },

    // Tabs
    Tabs: {
      horizontalMargin: `0 0 ${designTokens.spacing[6]}px 0`,
      itemActiveColor: designTokens.colors.primary[600],
      itemHoverColor: designTokens.colors.primary[500],
      itemSelectedColor: designTokens.colors.primary[600],
      inkBarColor: designTokens.colors.primary[600],
    },

    // Menu
    Menu: {
      itemBorderRadius: designTokens.radius.md,
      itemMarginBlock: designTokens.spacing[1],
      itemMarginInline: designTokens.spacing[1],
      itemPaddingInline: designTokens.spacing[3],
    },

    // Breadcrumb
    Breadcrumb: {
      itemColor: designTokens.colors.neutral[600],
      linkColor: designTokens.colors.neutral[600],
      linkHoverColor: designTokens.colors.primary[600],
      separatorColor: designTokens.colors.neutral[400],
    },

    // Pagination
    Pagination: {
      itemSize: 32,
      itemSizeSM: 24,
      itemActiveBg: designTokens.colors.primary[600],
      borderRadius: designTokens.radius.md,
    },

    // Switch
    Switch: {
      trackHeight: 24,
      trackMinWidth: 44,
      handleSize: 20,
    },

    // Checkbox
    Checkbox: {
      borderRadiusSM: designTokens.radius.sm,
    },

    // Radio
    Radio: {
      radioSize: 20,
    },

    // Tooltip
    Tooltip: {
      borderRadius: designTokens.radius.md,
    },

    // Popover
    Popover: {
      borderRadius: designTokens.radius.lg,
    },

    // Notification
    Notification: {
      borderRadius: designTokens.radius.lg,
    },

    // Message
    Message: {
      borderRadius: designTokens.radius.lg,
    },
  },
};

/**
 * Dark theme configuration (optional)
 */
export const antdDarkTheme: ThemeConfig = {
  ...antdTheme,
  token: {
    ...antdTheme.token,
    colorBgContainer: designTokens.colors.neutral[900],
    colorBgElevated: designTokens.colors.neutral[800],
    colorBgLayout: designTokens.colors.neutral[950],
    colorBorder: designTokens.colors.neutral[700],
    colorBorderSecondary: designTokens.colors.neutral[800],
    colorText: designTokens.colors.neutral[50],
    colorTextSecondary: designTokens.colors.neutral[300],
    colorTextTertiary: designTokens.colors.neutral[500],
    colorTextQuaternary: designTokens.colors.neutral[600],
  },
};
